import jwtDecode from "jwt-decode";

export interface ParsedDocumentQr {
  dni: string;
  country?: string;
  sex?: string;
  birthdate?: string;
  firstname?: string;
  lastname?: string;
  source: "jwt";
}

const JWT_SHAPE_REGEX = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const DNI_ALIASES = [
  "nroDocumento",
  "dni",
  "documento",
  "numeroDocumento",
  "nro_documento",
  "documentNumber",
  "docNumber",
];
const SEX_ALIASES = ["sexo", "sex", "genero", "gender"];
const BIRTHDATE_ALIASES = [
  "fechaNacimiento",
  "fecha_nacimiento",
  "birthdate",
  "birthDate",
  "dob",
  "fnac",
];
const FIRSTNAME_ALIASES = ["nombre", "nombres", "firstname", "firstName", "givenName"];
const LASTNAME_ALIASES = ["apellido", "apellidos", "lastname", "lastName", "familyName", "surname"];
const COUNTRY_ALIASES = ["pais", "country", "nacionalidad", "nationality"];

// Detección barata (solo regex, sin decodificar) para poder usarse en el loop
// caliente de decode de la cámara antes de comprometerse a un parseo completo.
export const isJwtShaped = (value: string): boolean => {
  if (!value) return false;
  return JWT_SHAPE_REGEX.test(value.trim());
};

const findClaim = (payload: Record<string, unknown>, aliases: string[]): string | undefined => {
  for (const key of Object.keys(payload)) {
    if (aliases.some((alias) => alias.toLowerCase() === key.toLowerCase())) {
      const value = payload[key];
      if (value !== undefined && value !== null) return String(value);
    }
  }
  // Búsqueda superficial en un nivel de objetos anidados: el schema real
  // todavía no está confirmado con una muestra física, así que esto es una
  // cobertura barata por si algún emisor envuelve los claims en un sub-objeto.
  for (const value of Object.values(payload)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = findClaim(value as Record<string, unknown>, aliases);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
};

// Parser autoritativo: re-valida la forma intentando decodificar de verdad
// (no solo el regex), y falla cerrado ante cualquier problema — nunca debe
// tirar una excepción no controlada porque se llama desde el loop de decode
// de la cámara y desde el buffer del lector HID.
export const parseJwtShapedQr = (value: string): ParsedDocumentQr | null => {
  try {
    const payload = jwtDecode<Record<string, unknown>>(value);
    if (!payload || typeof payload !== "object") return null;

    const dni = findClaim(payload, DNI_ALIASES);
    if (!dni) return null;

    return {
      dni: dni.trim(),
      sex: findClaim(payload, SEX_ALIASES),
      birthdate: findClaim(payload, BIRTHDATE_ALIASES),
      firstname: findClaim(payload, FIRSTNAME_ALIASES),
      lastname: findClaim(payload, LASTNAME_ALIASES),
      country: findClaim(payload, COUNTRY_ALIASES),
      source: "jwt",
    };
  } catch {
    return null;
  }
};

// Dispatcher / cadena priorizada. Hoy tiene un solo link (JWT), que cubre
// tanto el QR de Mi Argentina como -asumido, a confirmar con una muestra
// física real- el QR del dorso del DNI nuevo. Si aparece un formato con otra
// forma (URL de verificación, JSON plano, etc.) agregar acá una función
// isX/parseX más, ordenada de más específica a más genérica.
export const parseDocumentQr = (value: string): ParsedDocumentQr | null => {
  try {
    if (!value) return null;
    if (isJwtShaped(value)) return parseJwtShapedQr(value);
    return null;
  } catch {
    return null;
  }
};
